import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
    setModalViewActualTab,
    setAddEventMode,
    setDuplicatedEventId,
    toggleAddModal,
    closeEditModal,
    toggleNeedRefresh,
    clearDuplicatedEventId,
    clearPendingEditReview
} from '../../store/eventsSlice';
import Modal from '../../components/components/modal/modal';
import ApiService from '../../components/api_services/apiService';
import './Events.css';
import './ModalX.css';
import './Stepper.css';
import {
    isEventActive,
    isEventEnded,
    isPendingDeactivation,
    isPendingActivation,
    isDeactivationPayload,
    isActivationPayload,
} from '../../utils/eventStatus.js';

const TIME_ZONE_SUFFIX_PATTERN = /(Z|[+-]\d{2}:?\d{2})$/i;

const toDateInputValue = (date) => {
    const pad = (value) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const toTimeInputValue = (date) => {
    const pad = (value) => String(value).padStart(2, '0');
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const splitDateTimeValueForForm = (value) => {
    if (!value) {
        return { date: '', time: '' };
    }

    const rawValue = String(value).trim();
    if (!rawValue) {
        return { date: '', time: '' };
    }

    if (TIME_ZONE_SUFFIX_PATTERN.test(rawValue)) {
        const parsedDate = new Date(rawValue);
        if (!Number.isNaN(parsedDate.getTime())) {
            return {
                date: toDateInputValue(parsedDate),
                time: toTimeInputValue(parsedDate),
            };
        }
    }

    const [datePart = '', timePart = ''] = rawValue.split(/[T ]/);
    return {
        date: datePart,
        time: timePart.substring(0, 5),
    };
};

function AddEvent() {
    const dispatch = useDispatch();

    const addEventMode = useSelector((state) => state.events.addEventMode);

    const editedEvent = useSelector((state) => state.events.editedEvent);
    const pendingEditReview = useSelector((state) => state.events.pendingEditReview);
    const duplicatedEventId = useSelector((state) => state.events.duplicatedEventId);
    const isAddModalOpen = useSelector((state) => state.events.addModalOpen);
    const isEditModalOpen = useSelector((state) => state.events.editModalOpen);
    const modalViewActualTab = useSelector((state) => state.events.modalViewActualTab);
    // Stan dla zdjęcia głównego: { file: File | null, preview: string | null, id: string | null }
    const [mainImage, setMainImage] = useState({ file: null, preview: null, id: null });

    const accessToken = useSelector((state) => state.tokens.accessToken);

    // Stan dla zdjęć dodatkowych: tablica obiektów [{ file: File, preview: string, id: string | null }]
    const [additionalImages, setAdditionalImages] = useState([]);

    const modalViewTabs = [
        "Dane podstawowe",
        "Opis",
        "Multimedia",
    ]

    const [imageHashCode, setImageHashCode] = useState('');

    const MAX_FILE_SIZE = 5242880; // 5MB
    const TICKET_PAID = "P\u0142atny";
    const TICKET_FREE = "Bezp\u0142atny";

    const [formData, setFormData] = useState({
        name: '',
        category: '',
        startDate: '',
        eventTime: '',
        eventEndDate: '',
        eventEndTime: '',
        place: '',
        adres: '',
        city: 'Warszawa',
        ticketInfo: TICKET_FREE,
        ticketPrice: '0',
        description: '',
        zipCode: '',
        added_on_utc: '',
        isActive: true,
        tags: [],
    });
    const [originalFormData, setOriginalFormData] = useState(null);
    const [originalImageState, setOriginalImageState] = useState({ mainId: null, additionalIds: [] });
    const [hoveredChangedField, setHoveredChangedField] = useState(null);
    const [categories, setCategories] = useState([]);
    const [isTagModalOpen, setIsTagModalOpen] = useState(false);
    const [tagInput, setTagInput] = useState('');
    const [isStatusActionLoading, setIsStatusActionLoading] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const tagInputRef = useRef(null);
    const mainImageInputRef = useRef(null);
    const additionalImagesInputRef = useRef(null);
    const isPendingEditReviewMode = isEditModalOpen
        && pendingEditReview
        && String(pendingEditReview.eventId) === String(editedEvent);

    const getEventStatusLabel = (eventData) => {
        if (eventData?.isApprovedByAdmin === false) {
            return 'Oczekuj\u0105ce';
        }

        if (isEventEnded(eventData)) {
            return 'Zako\u0144czone';
        }

        return isEventActive(eventData) ? 'Aktywne' : 'Nieaktywne';
    };

    const getEventStatusClassName = (eventData) => {
        if (eventData?.isApprovedByAdmin === false) {
            return 'event-status event-status--pending';
        }

        if (isEventEnded(eventData)) {
            return 'event-status event-status--ended';
        }

        if (!isEventActive(eventData)) {
            return 'event-status event-status--inactive';
        }

        return 'event-status event-status--active';
    };

    const mapApiEventToFormData = (eventData = {}) => {
        const loadedPrice = eventData.ticketPrice ?? '';
        const numericLoadedPrice = Number(loadedPrice);
        const derivedTicketInfo = !Number.isNaN(numericLoadedPrice) && numericLoadedPrice === 0 ? TICKET_FREE : TICKET_PAID;
        const { date: startDate, time: eventTime } = splitDateTimeValueForForm(eventData.startDate);
        const { date: eventEndDate, time: eventEndTime } = splitDateTimeValueForForm(eventData.endDate);

        return {
            ...eventData,
            adres: eventData.address ?? eventData.adres ?? '',
            zipCode: eventData.postalCode ?? eventData.zipCode ?? '',
            category: String(eventData.categoryId ?? ''),
            startDate,
            eventEndDate,
            eventTime,
            eventEndTime,
            ticketInfo: eventData.ticketInfo ?? derivedTicketInfo,
            tags: Array.isArray(eventData.tags) ? eventData.tags : [],
            isActive: eventData.active ?? eventData.isActive ?? true,
        };
    };

    const applyPendingPayloadToFormData = (baseFormData, payload = {}) => {
        if (!payload || typeof payload !== 'object') {
            return baseFormData;
        }

        const next = { ...baseFormData };

        if (payload.name !== undefined) next.name = payload.name ?? '';
        if (payload.categoryId !== undefined) next.category = String(payload.categoryId ?? '');

        if (payload.startDate) {
            const { date, time } = splitDateTimeValueForForm(payload.startDate);
            next.startDate = date;
            next.eventTime = time;
        }

        if (payload.endDate) {
            const { date, time } = splitDateTimeValueForForm(payload.endDate);
            next.eventEndDate = date;
            next.eventEndTime = time;
        }

        if (payload.place !== undefined) next.place = payload.place ?? '';
        if (payload.address !== undefined) next.adres = payload.address ?? '';
        if (payload.postalCode !== undefined) next.zipCode = payload.postalCode ?? '';
        if (payload.city !== undefined) next.city = payload.city ?? '';
        if (payload.ticketInfo !== undefined) next.ticketInfo = payload.ticketInfo ?? TICKET_FREE;
        if (payload.ticketPrice !== undefined) next.ticketPrice = String(payload.ticketPrice ?? '');
        if (payload.description !== undefined) next.description = payload.description ?? '';
        if (Array.isArray(payload.tags)) next.tags = payload.tags;
        if (isDeactivationPayload(payload, baseFormData)) {
            next.isActive = false;
        } else if (isActivationPayload(payload, baseFormData)) {
            next.isActive = true;
        } else if (payload.isActive !== undefined) {
            next.isActive = Boolean(payload.isActive === true || payload.isActive === 'true' || payload.isActive === 1);
        } else if (payload.active !== undefined) {
            next.isActive = Boolean(payload.active === true || payload.active === 'true' || payload.active === 1);
        } else if (payload.is_active !== undefined) {
            next.isActive = Boolean(payload.is_active === true || payload.is_active === 'true' || payload.is_active === 1);
        }

        return next;
    };

    const normalizeFieldValue = (value) => {
        if (Array.isArray(value)) {
            return value.join('|');
        }
        if (value === null || value === undefined) {
            return '';
        }
        return String(value);
    };

    const normalizeImageId = (value) => {
        if (value === null || value === undefined) {
            return null;
        }
        const next = String(value);
        return next.length > 0 ? next : null;
    };

    const getIdFromImageValue = (value) => {
        if (value && typeof value === 'object') {
            return normalizeImageId(
                value.id
                ?? value.hash
                ?? value.hashCode
                ?? value.image_hash_code
                ?? value.imageHashCode
            );
        }
        return normalizeImageId(value);
    };

    const getPendingMainImageId = (payload = {}) => {
        const keys = [
            'image_hash_code',
            'imageHashCode',
            'mainImageHashCode',
            'mainImageHash',
            'mainImageId',
            'mainImage',
        ];

        for (const key of keys) {
            if (payload?.[key] === undefined) {
                continue;
            }
            const parsedId = getIdFromImageValue(payload[key]);
            if (parsedId) {
                return parsedId;
            }
        }

        return null;
    };

    const getPendingAdditionalImageIds = (payload = {}) => {
        const keys = [
            'additionalImageHashCodes',
            'additionalImagesHashCodes',
            'additionalImageIds',
            'additionalImages',
            'images',
            'eventImages',
            'imageHashCodes',
        ];

        for (const key of keys) {
            const value = payload?.[key];
            if (!Array.isArray(value)) {
                continue;
            }

            const ids = value
                .map(getIdFromImageValue)
                .filter(Boolean);

            if (ids.length > 0) {
                return [...new Set(ids)];
            }
        }

        return null;
    };

    const buildImagePreviewList = async (imageIds = []) => {
        const loadedImages = [];
        for (const img of imageIds) {
            const preview = await fetchImages(img);
            loadedImages.push({ file: null, preview, id: img });
        }
        return loadedImages;
    };

    const inferReviewImageState = (baseState, payload = {}) => {
        const pendingMainId = getPendingMainImageId(payload);
        const pendingAdditionalIds = getPendingAdditionalImageIds(payload);

        return {
            mainId: pendingMainId ?? baseState.mainId ?? null,
            additionalIds: pendingAdditionalIds ?? baseState.additionalIds,
        };
    };

    const fetchCategories = async () => {
        const response = await ApiService.getEventsCategories();
        setCategories(response.data);
    };

    const fetchImages = async (imgId) => {
        try {
            const imgResponse = await fetch(`/api/events-images/${imgId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
                credentials: 'include',
                mode: 'cors'
            });
            if (!imgResponse.ok) {
                throw new Error(`HTTP error! status: ${imgResponse.status}`);
            }
            const blob = await imgResponse.blob();
            // Tworzymy obiekt File, aby spełnić warunki walidacji (mainImage.file)
            // const file = new File([blob], "mainImage.jpg", {type: blob.type});
            const preview = URL.createObjectURL(blob);

            return preview;
        } catch (error) {
            console.error("Błąd pobierania zdjęcia głównego:", error);
        }
    }

    const fetchEvent = async () => {
        const response = await ApiService.getEvent(editedEvent);
        const loadedEvent = response.data || {};
        const defaultImage = normalizeImageId(response?.data?.image_hash_code);
        console.log(response.data);
        const mappedBaseData = mapApiEventToFormData(response.data);
        const reviewedData = isPendingEditReviewMode
            ? applyPendingPayloadToFormData(mappedBaseData, pendingEditReview?.payload)
            : mappedBaseData;
        const allEventImagesResponse = await ApiService.getAllImagesForEvent(editedEvent);
        const allEventImageIds = Array.isArray(allEventImagesResponse?.data)
            ? allEventImagesResponse.data.map(normalizeImageId).filter(Boolean)
            : [];
        const baseAdditionalIds = allEventImageIds.filter((imgId) => imgId !== defaultImage);
        const baseImageState = { mainId: defaultImage, additionalIds: baseAdditionalIds };
        const nextReviewImageState = isPendingEditReviewMode
            ? inferReviewImageState(baseImageState, pendingEditReview?.payload)
            : baseImageState;

        setOriginalFormData(mappedBaseData);
        setOriginalImageState(baseImageState);
        setFormData(reviewedData);
        setRejectionReason(
            String(
                loadedEvent.rejectReason
                ?? loadedEvent.rejectionReason
                ?? loadedEvent.reason
                ?? ''
            ).trim()
        );
        setImageHashCode(nextReviewImageState.mainId || '');
        if (nextReviewImageState.mainId) {
                const preview = await fetchImages(nextReviewImageState.mainId);
                setMainImage({ file: null, preview, id: nextReviewImageState.mainId });
        } else {
            setMainImage({ file: null, preview: null, id: null });
        }
        const loadedAdditionalImages = await buildImagePreviewList(nextReviewImageState.additionalIds);
        setAdditionalImages(loadedAdditionalImages);
    }

    const fetchEventForDuplicate = async (eventId) => {
        const response = await ApiService.getEvent(eventId);
        const defaultImage = response.data.image_hash_code;
        const loadedPrice = response.data.ticketPrice ?? '';
        const numericLoadedPrice = Number(loadedPrice);
        const derivedTicketInfo = !Number.isNaN(numericLoadedPrice) && numericLoadedPrice === 0 ? TICKET_FREE : TICKET_PAID;
        console.log(response.data);
        setFormData({
            ...response.data,
            added_on_utc: '',
            adres: response.data.address ?? response.data.adres ?? '',
            zipCode: response.data.postalCode ?? response.data.zipCode ?? '',
            category: String(response.data.categoryId),
            startDate: '',
            eventEndDate: '',
            // Wyciagamy HH:MM (bierzemy 5 pierwszych znakow po 'T')
            eventTime: '',
            eventEndTime: '',
            ticketInfo: derivedTicketInfo,
            tags: Array.isArray(response.data.tags) ? response.data.tags : [],
        });
        setImageHashCode(defaultImage || '');
        if (defaultImage) {
            const preview = await fetchImages(defaultImage);
            setMainImage({ file: null, preview, id: defaultImage });
        } else {
            setMainImage({ file: null, preview: null, id: null });
        }
        setAdditionalImages([]);
        setRejectionReason('');
    }

    const fetchEventPhotos = async () => {
        if (isPendingEditReviewMode) {
            return;
        }

        const response = await ApiService.getAllImagesForEvent(editedEvent);
        console.log(response);
        const images = response.data;
        const loadedImages = [];
        if (images.length > 0) {
            for (const img of images) {

                if (!(imageHashCode === img)) {
                    console.log(img + "  vs  " + imageHashCode);
                    // Pobieramy blob (zakładam, że fetchImages zwraca URL lub blob URL)
                    const preview = await fetchImages(img);

                    // Dodajemy do lokalnej tablicy zamiast od razu do stanu
                    loadedImages.push({file: null, preview: preview, id: img});
                }
            }
        }

        // Aktualizujemy stan TYLKO RAZ, nadpisując wszystko nową listą
        // Dzięki temu unikamy duplikatów i zbędnych przerysowań
        setAdditionalImages(loadedImages);
    };


    useEffect(() => {
        const loadData = async () => {
            await fetchCategories();
            if (isEditModalOpen) {
                await fetchEvent();
                // fetchEventPhotos() będzie się wykonać po fetchEvent()
            }
        };

        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditModalOpen, pendingEditReview]);

    useEffect(() => {
        if (isAddModalOpen && duplicatedEventId) {
            fetchCategories();
            fetchEventForDuplicate(duplicatedEventId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAddModalOpen, duplicatedEventId]);

    useEffect(() => {
        if (isAddModalOpen && !isEditModalOpen && !duplicatedEventId) {
            setFormData((prev) => ({
                ...prev,
                eventTime: '',
                eventEndTime: '',
                ticketInfo: TICKET_FREE,
                ticketPrice: "0",
            }));
        }
    }, [isAddModalOpen, isEditModalOpen, duplicatedEventId]);

// Osobny useEffect dla zdjęć dodatkowych
    useEffect(() => {
        if (isEditModalOpen && imageHashCode && !isPendingEditReviewMode) {
            fetchEventPhotos();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageHashCode, isEditModalOpen, isPendingEditReviewMode]);


    useEffect(() => {
        const isValid =
            formData.name &&
            formData.category &&
            formData.startDate &&
            formData.eventTime &&
            formData.eventEndDate &&
            formData.eventEndTime &&
            formData.startDate <= formData.eventEndDate &&
            formData.place &&
            formData.city &&
            formData.ticketInfo &&
            (
                formData.ticketInfo === TICKET_FREE ||
                (formData.ticketPrice !== '' && Number(formData.ticketPrice) !== 0)
            ) &&
            formData.description;

        setIsSubmitDisabled(!isValid);
    }, [formData, mainImage, additionalImages]);

    useEffect(() => {
        if (!isEditModalOpen || formData.ticketPrice === '') {
            return;
        }

        const numericPrice = Number(formData.ticketPrice);
        if (Number.isNaN(numericPrice)) {
            return;
        }

        if (numericPrice === 0 && formData.ticketInfo !== TICKET_FREE) {
            setFormData((prev) => ({ ...prev, ticketInfo: TICKET_FREE }));
            return;
        }

        if (numericPrice !== 0 && formData.ticketInfo !== TICKET_PAID) {
            setFormData((prev) => ({ ...prev, ticketInfo: TICKET_PAID }));
        }
    }, [isEditModalOpen, formData.ticketPrice, formData.ticketInfo]);

    useEffect(() => {
        if (isTagModalOpen) {
            setTimeout(() => tagInputRef.current?.focus(), 0);
        }
    }, [isTagModalOpen]);

    // --- NOWA LOGIKA ZDJĘĆ ---
    const defaultPlaceholder = "./x3.jpg";

    const handleDuplikujClick = () => {
        dispatch(setAddEventMode(true));
        dispatch(setModalViewActualTab("Dane podstawowe"));
        dispatch(setDuplicatedEventId(editedEvent));
        dispatch(closeEditModal());
        dispatch(toggleAddModal());
    }

    // Nowa funkcja do usuwania zdjęcia
    const handleRemoveImage = (indexToRemove) => {
        setAdditionalImages(prev => prev.filter((_, index) => index !== indexToRemove));
        if (isEditModalOpen) {
            const id = additionalImages[indexToRemove].id;
            console.log("usuwam " + id);
            ApiService.deleteEventImage(id);
        }
    };

    // Handler dla wyboru zdjęcia głównego
    const handleMainImageChange = async (source) => {
        const file = source?.target ? source.target.files[0] : source;

        if (file && file.size > MAX_FILE_SIZE) {
            alert("Plik jest zbyt duży. Maksymalna wielkość to 5 MB.");
            if (source?.target) {
                source.target.value = ""; // Reset inputa
            }
            return;
        }

        let imageHashCode;
        if (isEditModalOpen) {
            imageHashCode = await ApiService.uploadEventImage(editedEvent, file);
            setImageHashCode(imageHashCode);
            await saveMainImage(imageHashCode);
        }
        if (file) {
            setMainImage({
                file: file,
                preview: URL.createObjectURL(file),
                id: imageHashCode
            });
        }
        if (source?.target) {
            source.target.value = "";
        }
    };

    // Handler dla wyboru wielu zdjęć dodatkowych
    const handleAdditionalImagesChange = async (source) => {
        const files = source?.target
            ? Array.from(source.target.files)
            : Array.from(source || []);

        if (files.length > 0) {
            const newImages = [];
            for (const file of files) {
                if (file.size > MAX_FILE_SIZE) {
                    alert(`Plik "${file.name}" jest zbyt duży. Został pominięty. Maksymalna wielkość to 5 MB.`);
                    continue;
                }

                let imageHashCode;
                if (isEditModalOpen) {
                    imageHashCode = await ApiService.uploadEventImage(editedEvent, file);
                }
                newImages.push({
                    file: file,
                    preview: URL.createObjectURL(file),
                    id: imageHashCode
                });
            }
            setAdditionalImages(prev => [...prev, ...newImages]);
        }
        if (source?.target) {
            source.target.value = "";
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e, kind) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer?.files || []);
        if (!droppedFiles.length) return;

        if (kind === "main") {
            handleMainImageChange(droppedFiles[0]);
            return;
        }

        handleAdditionalImagesChange(droppedFiles);
    };

    // Funkcja zamieniająca zdjęcie dodatkowe z głównym
    const handleSwapImage = async (indexToSwap) => {
        const imageFromAdditional = additionalImages[indexToSwap];
        const currentMain = mainImage;

        // 1) ZAWSZE aktualizujemy stan lokalny (działa i dla dodawania, i dla edycji)
        setMainImage(imageFromAdditional);

        // 2) Tylko w trybie edycji synchronizujemy backend (mamy już istniejący event + id obrazka)
        if (isEditModalOpen) {
            const newMainHash = imageFromAdditional?.id;

            // Jeśli obrazek pochodzi z pliku i nie ma jeszcze id (hash),
            // to nie ma czego zapisać jako main na backendzie na tym etapie.
            if (newMainHash) {
                setImageHashCode(newMainHash);
                await saveMainImage(newMainHash);
            }
        }

        // 3) Aktualizujemy listę dodatkowych (swap / replace)
        const newAdditionalList = [...additionalImages];

        if (currentMain.file || currentMain.preview) {
            newAdditionalList[indexToSwap] = currentMain;
        } else {
            newAdditionalList.splice(indexToSwap, 1);
        }

        setAdditionalImages(newAdditionalList);
    };
    // -------------------------

    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

    const handleInputChange = (e) => {
        // Usuwamy st?d logik? walidacji, zostawiamy tylko aktualizacj? stanu
        const { name, value } = e.target;
        setFormData((prev) => {
            if (name === "startDate" && prev.eventEndDate && value && value > prev.eventEndDate) {
                return { ...prev, [name]: value, eventEndDate: "" };
            }

            return { ...prev, [name]: value };
        });
        console.log(formData);
    };

    const handleTicketInfoSelect = (value) => {
        setFormData((prev) => ({
            ...prev,
            ticketInfo: value,
            ticketPrice: value === TICKET_FREE ? "0" : "",
        }));
    };

    const getTimeParts = (value) => {
        const [hour = "00", minute = "00"] = String(value || "00:00").split(":");
        return { hour, minute };
    };

    const handleSplitTimeChange = (field, part, rawValue) => {
        const cleaned = rawValue.replace(/\D/g, "").slice(0, 2);

        setFormData((prev) => {
            const { hour, minute } = getTimeParts(prev[field]);
            const nextHour = part === "hour" ? cleaned : hour;
            const nextMinute = part === "minute" ? cleaned : minute;

            return {
                ...prev,
                [field]: `${nextHour}:${nextMinute}`,
            };
        });
    };

    const handleSplitTimeBlur = (field) => {
        const normalizePart = (value, max) => {
            if (value === "") {
                return "00";
            }
            const parsed = Number.parseInt(value, 10);
            if (Number.isNaN(parsed)) {
                return "00";
            }
            return String(Math.min(parsed, max)).padStart(2, "0");
        };

        setFormData((prev) => {
            const { hour, minute } = getTimeParts(prev[field]);
            const normalizedHour = normalizePart(hour, 23);
            const normalizedMinute = normalizePart(minute, 59);
            return {
                ...prev,
                [field]: `${normalizedHour}:${normalizedMinute}`,
            };
        });
    };

    const isTwoStepFlow = addEventMode || isEditModalOpen;
    const isSecondStepActive =
        modalViewActualTab === "Opis"
        || (isTwoStepFlow && modalViewActualTab === "Multimedia");

    const handleNextButtonClick = () => {
        if (isTwoStepFlow) {
            if (modalViewActualTab === "Dane podstawowe") {
                dispatch(setModalViewActualTab("Opis"));
            }
            return;
        }

        const actual = modalViewTabs.indexOf(modalViewActualTab);
        if (actual < modalViewTabs.length - 1) {
            dispatch(setModalViewActualTab(modalViewTabs[actual + 1]));
        }
    }

    const handlePrevButtonClick = () => {
        if (isTwoStepFlow) {
            if (modalViewActualTab !== "Dane podstawowe") {
                dispatch(setModalViewActualTab("Dane podstawowe"));
            }
            return;
        }

        const actual = modalViewTabs.indexOf(modalViewActualTab);
        if (actual > 0) {
            dispatch(setModalViewActualTab(modalViewTabs[actual - 1]));
        }
    }

    const handleToggleEventActive = async () => {
        if (!isEditModalOpen || !editedEvent || isStatusActionLoading || isEventEnded(formData)) {
            return;
        }

        try {
            setIsStatusActionLoading(true);
            const nextActive = !formData.isActive;
            await ApiService.updateEvent(editedEvent, { isActive: nextActive });
            setFormData((prev) => ({ ...prev, isActive: nextActive }));
            dispatch(toggleNeedRefresh());
        } catch (error) {
            console.error('B\u0142\u0105d zmiany statusu wydarzenia:', error);
        } finally {
            setIsStatusActionLoading(false);
        }
    };

    const handleApproveEvent = async () => {
        if (!isEditModalOpen || !editedEvent || formData?.isApprovedByAdmin !== false || isStatusActionLoading) {
            return;
        }

        try {
            setIsStatusActionLoading(true);
            await ApiService.approveEvent(editedEvent);
            await fetchEvent();
            dispatch(toggleNeedRefresh());
        } catch (error) {
            console.error('Błąd akceptacji wydarzenia:', error);
        } finally {
            setIsStatusActionLoading(false);
        }
    };

    const promptRejectReason = (initialReason = '') => {
        const value = window.prompt('Podaj powód odrzucenia:', initialReason);
        if (value === null) {
            return null;
        }

        const reason = String(value).trim();
        if (!reason) {
            alert('Powód odrzucenia jest wymagany.');
            return null;
        }

        return reason;
    };

    const handleRejectEvent = async () => {
        if (!isEditModalOpen || !editedEvent || formData?.isApprovedByAdmin !== false || isStatusActionLoading) {
            return;
        }

        const reason = promptRejectReason(rejectionReason);
        if (!reason) {
            return;
        }

        try {
            setIsStatusActionLoading(true);
            await ApiService.rejectEvent({ id: String(editedEvent), reason });
            setRejectionReason(reason);
            await fetchEvent();
            dispatch(toggleNeedRefresh());
        } catch (error) {
            console.error('Błąd odrzucenia wydarzenia:', error);
        } finally {
            setIsStatusActionLoading(false);
        }
    };

    const handleApprovePendingEditFromReview = async () => {
        if (!isPendingEditReviewMode || !editedEvent || isStatusActionLoading) {
            return;
        }

        try {
            setIsStatusActionLoading(true);
            await ApiService.approvePendingEventEdit(editedEvent);
            dispatch(toggleNeedRefresh());
            dispatch(clearPendingEditReview());
            dispatch(closeEditModal());
        } catch (error) {
            console.error('Błąd akceptacji zmiany wydarzenia:', error);
        } finally {
            setIsStatusActionLoading(false);
        }
    };

    const handleRejectPendingEditFromReview = async () => {
        if (!isPendingEditReviewMode || !editedEvent || isStatusActionLoading) {
            return;
        }

        const reason = promptRejectReason();
        if (!reason) {
            return;
        }

        try {
            setIsStatusActionLoading(true);
            await ApiService.rejectPendingEventEdit({ id: editedEvent, reason });
            dispatch(toggleNeedRefresh());
            dispatch(clearPendingEditReview());
            dispatch(closeEditModal());
        } catch (error) {
            console.error('Błąd odrzucenia zmiany wydarzenia:', error);
        } finally {
            setIsStatusActionLoading(false);
        }
    };

    const closeTagModal = () => {
        setIsTagModalOpen(false);
        setTagInput('');
    };

    const handleAddTag = () => {
        const value = tagInput.trim();
        if (!value) {
            return;
        }
        setFormData((prev) => {
            if (prev.tags.includes(value)) {
                return prev;
            }
            return { ...prev, tags: [...prev.tags, value] };
        });
        closeTagModal();
    };

    const handleRemoveTag = (indexToRemove) => {
        setFormData((prev) => ({
            ...prev,
            tags: prev.tags.filter((_, index) => index !== indexToRemove),
        }));
    };

    const isLastTab = isTwoStepFlow
        ? isSecondStepActive
        : modalViewTabs.indexOf(modalViewActualTab) === modalViewTabs.length - 1;
    const isFirstTab = modalViewActualTab === "Dane podstawowe";
    const isPendingApproval = formData?.isApprovedByAdmin === false;

    const isPendingDeactivationRequest = isPendingEditReviewMode && (
        isPendingDeactivation(pendingEditReview?.payload, originalFormData) ||
        isPendingDeactivation(pendingEditReview, originalFormData) ||
        (originalFormData?.isActive === true && formData.isActive === false)
    );

    const isPendingActivationRequest = isPendingEditReviewMode && !isPendingDeactivationRequest && (
        isPendingActivation(pendingEditReview?.payload, originalFormData) ||
        isPendingActivation(pendingEditReview, originalFormData) ||
        (originalFormData?.isActive === false && formData.isActive === true)
    );

    const pendingReviewChangedFields = {
        name: isPendingEditReviewMode && normalizeFieldValue(formData.name) !== normalizeFieldValue(originalFormData?.name),
        category: isPendingEditReviewMode && normalizeFieldValue(formData.category) !== normalizeFieldValue(originalFormData?.category),
        startDate: isPendingEditReviewMode && normalizeFieldValue(formData.startDate) !== normalizeFieldValue(originalFormData?.startDate),
        eventTime: isPendingEditReviewMode && normalizeFieldValue(formData.eventTime) !== normalizeFieldValue(originalFormData?.eventTime),
        eventEndDate: isPendingEditReviewMode && normalizeFieldValue(formData.eventEndDate) !== normalizeFieldValue(originalFormData?.eventEndDate),
        eventEndTime: isPendingEditReviewMode && normalizeFieldValue(formData.eventEndTime) !== normalizeFieldValue(originalFormData?.eventEndTime),
        place: isPendingEditReviewMode && normalizeFieldValue(formData.place) !== normalizeFieldValue(originalFormData?.place),
        adres: isPendingEditReviewMode && normalizeFieldValue(formData.adres) !== normalizeFieldValue(originalFormData?.adres),
        zipCode: isPendingEditReviewMode && normalizeFieldValue(formData.zipCode) !== normalizeFieldValue(originalFormData?.zipCode),
        city: isPendingEditReviewMode && normalizeFieldValue(formData.city) !== normalizeFieldValue(originalFormData?.city),
        ticketInfo: isPendingEditReviewMode && normalizeFieldValue(formData.ticketInfo) !== normalizeFieldValue(originalFormData?.ticketInfo),
        ticketPrice: isPendingEditReviewMode && normalizeFieldValue(formData.ticketPrice) !== normalizeFieldValue(originalFormData?.ticketPrice),
        description: isPendingEditReviewMode && normalizeFieldValue(formData.description) !== normalizeFieldValue(originalFormData?.description),
        tags: isPendingEditReviewMode && normalizeFieldValue(formData.tags) !== normalizeFieldValue(originalFormData?.tags),
        isActive: isPendingEditReviewMode && (
            formData.isActive !== originalFormData?.isActive ||
            isPendingDeactivationRequest ||
            isPendingActivationRequest
        ),
        mainImage: isPendingEditReviewMode
            && normalizeImageId(imageHashCode || mainImage?.id)
                !== normalizeImageId(originalImageState?.mainId),
    };

    const hasMainImageChange = pendingReviewChangedFields.mainImage === true;
    const pendingUnsupportedFieldLabels = isPendingEditReviewMode
        && pendingEditReview?.payload?.ticketPrice !== undefined
        && normalizeFieldValue(formData.ticketPrice) !== normalizeFieldValue(originalFormData?.ticketPrice)
        ? ['cena biletu (backend nie obsługuje zapisu tej zmiany)']
        : [];
    const pendingChangedFieldLabels = [
        ['name', 'nazwa wydarzenia'],
        ['category', 'kategoria'],
        ['startDate', 'data rozpoczęcia'],
        ['eventTime', 'godzina rozpoczęcia'],
        ['eventEndDate', 'data zakończenia'],
        ['eventEndTime', 'godzina zakończenia'],
        ['place', 'nazwa miejsca'],
        ['adres', 'adres'],
        ['zipCode', 'kod pocztowy'],
        ['city', 'miasto'],
        ['ticketInfo', 'informacje o biletach'],
        ['description', 'opis'],
        ['tags', 'tagi'],
        ['mainImage', 'zdjęcie główne'],
    ]
        .filter(([fieldName]) => pendingReviewChangedFields[fieldName])
        .map(([, label]) => label);
    const pendingStatusChangeLabel = isPendingDeactivationRequest
        ? 'dezaktywacja wydarzenia (zmiana statusu na Nieaktywne)'
        : isPendingActivationRequest
        ? 'aktywacja wydarzenia (zmiana statusu na Aktywne)'
        : pendingReviewChangedFields.isActive
        ? (formData.isActive ? 'aktywacja wydarzenia' : 'dezaktywacja wydarzenia')
        : null;
    const pendingChangeSummary = [...pendingChangedFieldLabels, pendingStatusChangeLabel, ...pendingUnsupportedFieldLabels].filter(Boolean);

    const getDisplayFieldValue = (fieldName) => {
        if (!pendingReviewChangedFields[fieldName]) {
            return formData[fieldName];
        }

        if (hoveredChangedField === fieldName) {
            return originalFormData?.[fieldName] ?? '';
        }

        return formData[fieldName];
    };

    const getChangedFieldClassName = (baseClassName, fieldName) => {
        if (!pendingReviewChangedFields[fieldName]) {
            return baseClassName;
        }
        return `${baseClassName} pending-edit-field-changed`;
    };

    const getChangedFieldEvents = (fieldName) => {
        if (!pendingReviewChangedFields[fieldName]) {
            return {};
        }
        return {
            onMouseEnter: () => setHoveredChangedField(fieldName),
            onMouseLeave: () => setHoveredChangedField(null),
            title: `Nowa wartosc. Najedz, aby zobaczyc poprzednia: ${originalFormData?.[fieldName] ?? ''}`,
        };
    };

    const addEventStep = modalViewActualTab === "Dane podstawowe" ? 1 : 2;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isPendingEditReviewMode) {
            await handleApprovePendingEditFromReview();
            return;
        }

        try {
            // 2. Przygotowanie danych formularza z IDkami zdjęć
            const data = {
                name: formData.name,
                place: formData.place,
                city: formData.city,
                categoryId: Number(formData.category),
                startDate: `${formData.startDate}T${formData.eventTime}:00`,
                endDate: `${formData.eventEndDate}T${formData.eventEndTime}:00`,
                ticketInfo: formData.ticketInfo,
                // Backend obecnie nie obsługuje aktualizacji ticketPrice.
                ...(isAddModalOpen ? { ticketPrice: formData.ticketPrice } : {}),
                description: formData.description,
                address: formData.adres,
                postalCode: formData.zipCode,
                tags: formData.tags,
                isActive: formData.isActive,
                image_hash_code: imageHashCode,
            };


            let response;
            if (isEditModalOpen) {
                response = await ApiService.updateEvent(editedEvent, data);
                dispatch(closeEditModal());
            }
            if (isAddModalOpen) {
                response = await ApiService.addNewEvent(data);

                // Zapis fotografii dla nowego eventu
                if (response.id) {
                    const eventId = response.id;
                    console.log('Nowe wydarzenie zostało dodane:', eventId);

                    console.log("MainImage " + mainImage.file);

                    // Zapisz główne zdjęcie
                    if (mainImage.file) {
                        const mainHash = await ApiService.uploadEventImage(eventId, mainImage.file);
                        await ApiService.updateEvent(eventId, { image_hash_code: mainHash });
                    }

                    // Zapisz dodatkowe zdjęcia
                    for (const img of additionalImages) {
                        if (img.file) {
                            await ApiService.uploadEventImage(eventId, img.file);
                        }
                    }
                }
                dispatch(toggleAddModal());
            }
            console.log('Wydarzenie zostało dodane/zaktualizowane:', response)
            dispatch(toggleNeedRefresh());
            dispatch(clearDuplicatedEventId());

            // Reset formularza i zdjęć (opcjonalnie)
            setMainImage({ file: null, preview: null, id: null });
            setAdditionalImages([]);

        } catch (error) {
            console.error('Błąd podczas dodawania nowego wydarzenia:', error);
        }
    };

    const saveMainImage = async (imageHashCode) => {

        try {
            // 2. Przygotowanie danych formularza z IDkami zdjęć
            const data = {
                id: editedEvent,
                image_hash_code: imageHashCode
            };

            let response;
            if (isEditModalOpen) {
                response = await ApiService.updateEvent(editedEvent, data);
            }
            console.log('Wydarzenie zostało zaktualizowane:', response)
            dispatch(toggleNeedRefresh());

        } catch (error) {
            console.error('Błąd podczas dodawania nowego wydarzenia:', error);
        }
    };

    const handleCloseModal = () => {
        if (isAddModalOpen) {
            dispatch(toggleAddModal());
        }
        if (isEditModalOpen) {
            dispatch(closeEditModal());
        }
        dispatch(clearDuplicatedEventId());
        dispatch(clearPendingEditReview());
        closeTagModal();
        setRejectionReason('');
        setHoveredChangedField(null);
    };

    return (
        <>
        {isTagModalOpen && (
            <Modal
                isOpen={isTagModalOpen}
                onClose={closeTagModal}
                overlayClassName="modal-overlay-top"
                className="modal-content-small"
            >
                <h2>Dodaj tag</h2>
                <div className="flex-container-space-between border-bottom-light">
                    <label htmlFor="tagInput" className="padding-right-20">Nowy tag</label>
                    <input
                        ref={tagInputRef}
                        autoFocus
                        className=""
                        type="text"
                        id="tagInput"
                        name="tagInput"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddTag();
                            }
                        }}
                        title="Wpisz tag"
                    />
                </div>
                <div className="modal-margin-top-20 flex-container-center flex-start text-black">
                    <button className="button-orange-rounded-tiny-textonly text-black" type="button" onClick={closeTagModal}>
                        Anuluj
                    </button>
                    <button className="button-orange-rounded-tiny-textonly text-black" type="button" onClick={handleAddTag}>
                        Dodaj
                    </button>
                </div>
            </Modal>
        )}

        {/* sekcja legacy (bez nowego trybu dodawania) */}
        { !addEventMode && !isEditModalOpen && <Modal isOpen={isAddModalOpen || isEditModalOpen} onClose={handleCloseModal}>
            <h2>{isAddModalOpen ? "Dodaj nowe " : ""} {isEditModalOpen ? "Edytuj ": ""}wydarzenie</h2>
            <div className="userView-menu">
                {
                    modalViewTabs.map((tab, index) => {
                        return (
                            <button
                                type={modalViewActualTab === tab ? "modal-menu-active" : "modal-menu"}
                                id={index}
                                key={index}
                                className={modalViewActualTab === tab ? "buttons-menu-modal-1" : "buttons-menu-modal-2"}
                                onClick= {() => dispatch(setModalViewActualTab(tab))}>{tab}</button>)

                    })
                }
            </div>
            <form onSubmit={handleSubmit}>
                { modalViewActualTab === "Dane podstawowe" &&
                    <>
                        <div className="flex-container-space-between border-bottom-light">
                            <label htmlFor="name" className="padding-right-20">Nazwa wydarzenia</label>
                            <input
                                className="input-field"
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                title="Wprowadź nazwę wydarzenia"
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="flex-container-space-between border-bottom-light">
                            <label htmlFor="category" className="padding-right-20">Kategoria wydarzenia</label>
                            <select
                                className="input-field"
                                type="number"
                                id="category"
                                name="category"
                                value={formData.category}
                                title="Wybierz kategorię wydarzenia"
                                onChange={handleInputChange}
                                required
                            >
                                <option value="" disabled>Wybierz z listy</option>
                                { categories.map(category => (
                                    <option key={category.id} value={category.id}>{category.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex-container-space-between border-bottom-light">
                            <div>
                                <label htmlFor="startDate" className="padding-right-20">Data wydarzenia</label>
                                <input
                                    className="input-field"
                                    type="date"
                                    id="startDate"
                                    name="startDate"
                                    value={formData.startDate}
                                    onChange={handleInputChange}
                                    title="Wybierz datę wydarzenia"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="eventTime" className="padding-right-20">Godzina wydarzenia<span className="required-star">*</span></label>
                                <input
                                    className="input-field"
                                    type="time"
                                    id="eventTime"
                                    name="eventTime"
                                    value={formData.eventTime}
                                    onChange={handleInputChange}
                                    required
                                    title="Wybierz godzinę wydarzenia (np. 10:10)"
                                />
                            </div>
                            <div>
                                <label htmlFor="eventEndDate" className="padding-right-20">Data zakończenia</label>
                                <input
                                    className="input-field"
                                    type="date"
                                    id="eventEndDate"
                                    name="eventEndDate"
                                    value={formData.eventEndDate}
                                    onChange={handleInputChange}
                                    title="Wybierz datę zakończenia wydarzenia"
                                    min={formData.startDate || undefined}
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="eventEndTime" className="padding-right-20">Godzina zakończenia<span className="required-star">*</span></label>
                                <input
                                    className="input-field"
                                    type="time"
                                    id="eventEndTime"
                                    name="eventEndTime"
                                    value={formData.eventEndTime}
                                    onChange={handleInputChange}
                                    required
                                    title="Wybierz godzinę zakończenia (np. 12:30)"
                                />
                            </div>
                        </div>
                        <div className="flex-container-space-between border-bottom-light">
                            <label htmlFor="ticketInfo" className="padding-right-20">Bilety</label>
                            <input
                                className="input-field"
                                type="text"
                                id="ticketInfo"
                                name="ticketInfo"
                                value={formData.ticketInfo}
                                onChange={handleInputChange}
                                title="Wpisz informacje o biletach"
                                required
                            />
                        </div>
                        <div className="flex-container border-bottom-light">
                            <label htmlFor="ticketPrice" className="padding-right-20">Cena{formData.ticketInfo === TICKET_PAID && <span className="required-star">*</span>}</label>
                            <input
                                className="input-field"
                                type="number"
                                id="ticketPrice"
                                name="ticketPrice"
                                value={formData.ticketPrice}
                                title="Wprowadź minimalną cenę biletu"
                                onChange={handleInputChange}
                                required={formData.ticketInfo === TICKET_PAID}
                            />
                        </div>
                        <div className="header-group border-bottom">Lokalizacja</div>

                        <div className="flex-container-space-between border-bottom-light">
                            <label htmlFor="place" className="padding-right-20">Nazwa miejsca</label>
                            <input
                                className="input-field"
                                type="text"
                                id="place"
                                name="place"
                                value={formData.place}
                                title="Wprowadź nazwę miejca"
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="flex-container-space-between">
                            <label htmlFor="adres" className="padding-right-20">Adres</label>
                            <input
                                className="input-field"
                                type="text"
                                id="adres"
                                name="adres"
                                value={formData.adres}
                                title="Wprowadź adres"
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="flex-container-space-between border-bottom-light">
                            <div>
                                <label htmlFor="zipCode" className="padding-right-20">Kod pocztowy</label>
                                <input
                                    className="input-field"
                                    type="text"
                                    id="zipCode"
                                    name="zipCode"
                                    value={formData.zipCode}
                                    title="Wprowadź kod pocztowy"
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="city" className="padding-right-20">Miejscowość</label>
                                <input
                                    className="input-field"
                                    type="text"
                                    id="city"
                                    name="city"
                                    value={formData.city}
                                    title="Wprowadź miejscowość"
                                    onChange={handleInputChange}
                                    required
                                    readOnly={true}
                                />
                            </div>
                        </div>
                    </>}

                {modalViewActualTab === "Opis" &&
                    <>
                        <label htmlFor="description">Opis<span className="required-star">*</span></label>
                        <div className="flex-line border-bottom-light" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <textarea
                                type="text"
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                required
                                className="event-description"
                                style={{ flexGrow: 1 }}
                            />
                        </div>
                        <div className="flex-line border-bottom-light add-event-tags-row" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
                            <label className="padding-right-20" htmlFor="tagListEdit">Tagi</label>
                            <div id="tagListEdit" className="add-event-tags-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', flexGrow: 1 }}>
                                {formData.tags.length > 0 ? (
                                    formData.tags.map((tag, index) => (
                                        <span key={`${tag}-${index}`} style={{ border: '1px solid #ccc', borderRadius: '12px', padding: '2px 8px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                            {tag}
                                            <button
                                                type="button"
                                                className="button-orange-rounded-tiny-textonly"
                                                onClick={() => handleRemoveTag(index)}
                                                title="Usun tag"
                                            >
                                                x
                                            </button>
                                        </span>
                                    ))
                                ) : (
                                    <span>Brak tagow</span>
                                )}
                            </div>
                            <button
                                type="button"
                                className="button-orange-rounded-tiny-plus"
                                onClick={() => setIsTagModalOpen(true)}
                                title="Dodaj tag"
                            >
                                +
                            </button>
                        </div>
                    </>}



                {modalViewActualTab === "Multimedia" &&
                    <>
                        <div className="flex-line border-bottom-light">
                            <label htmlFor="mainImageInput" className="padding-right-20">Zdjęcie główne<span className="required-star">*</span></label>
                            <img
                                src={mainImage.preview || defaultPlaceholder}
                                className="imgMiniature"
                                alt="Podgląd głównego zdjęcia"
                            />
                            <input
                                className="input-field"
                                type="file"
                                id="mainImageInput"
                                name="mainImageInput"
                                onChange={handleMainImageChange}
                                title="Wybierz plik obrazka głównego"
                                accept="image/png, image/jpeg, image/webp"
                            />
                        </div>
                        <div className="flex-line border-bottom-light">
                            <label htmlFor="additionalImagesInput" className="padding-right-20">Zdjęcia dodatkowe</label>
                            <input
                                className="input-field"
                                type="file"
                                id="additionalImagesInput"
                                name="additionalImagesInput"
                                onChange={handleAdditionalImagesChange}
                                multiple // Obsługa wielu plików
                                title="Wybierz pliki obrazków dodatkowych"
                                accept="image/png, image/jpeg, image/webp"
                            />
                        </div>
                        <div className="flex-line2 border-bottom-light">
                            {additionalImages.length > 0 ? (
                                additionalImages.map((imgData, index) => (
                                    <div key={index} className="image-container">
                                        <img
                                            src={imgData.preview}
                                            className="imgMiniature"
                                            alt={`Dodatkowe ${index}`}
                                        />

                                        {/* Nakładka widoczna po najechaniu myszką */}
                                        <div className="image-overlay ">
                                            {/* Górna część: Usuń */}
                                            <div
                                                className="overlay-action overlay-action-top  delete"
                                                onClick={() => handleRemoveImage(index)}
                                                title="Usuń to zdjęcie"
                                            >
                                                Usuń
                                            </div>

                                            {/* Dolna część: Ustaw główne */}
                                            <div
                                                className="overlay-action overlay-action-bottom set-main"
                                                onClick={() => handleSwapImage(index)}
                                                title="Ustaw jako zdjęcie główne"
                                            >
                                                Główne
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                // Wyświetlanie placeholderów gdy brak zdjęć dodatkowych
                                <>
                                    {/*<img src={defaultPlaceholder} className="imgMiniature" alt="puste" />*/}
                                </>
                            )}
                        </div>

                    </>
                }

                    <div className="modal-margin-top-20 modal-actions">
                        <div className="modal-actions-left">
                            <button className="button-orange-rounded" type="button" onClick={handleCloseModal}>
                                Anuluj
                            </button>
                            <button className="button-orange-rounded" disabled={isFirstTab} type="button" onClick={handlePrevButtonClick}>
                                <span className="fontX">&larr;&nbsp;</span>Wstecz
                            </button>
                        </div>
                        <div className="modal-actions-right">
                            <button className="button-orange-rounded" disabled={isLastTab} type="button" onClick={handleNextButtonClick}>
                                Dalej<span className="fontX">&nbsp;&rarr;</span>
                            </button>
                            {isEditModalOpen  &&
                            <button className="button-orange-backgrounded" disabled={isLastTab} type="button" onClick={handleDuplikujClick}>
                                Duplikuj
                            </button>}

                            <button type="submit" disabled={isSubmitDisabled} className="button-orange-backgrounded">
                                {/*<span className="fontX">&#8862;&nbsp;</span>*/}
                                <div className="step-box-plus"></div>
                                {isAddModalOpen ? "Dodaj wydarzenie" : ""} {isEditModalOpen ? "Zapisz zmiany": ""}
                            </button>
                        </div>
                    </div>
            </form>
        </Modal> }

        {/*  sekcja dodawania  */}
            { (addEventMode || isEditModalOpen) && <Modal className={`modal-content-add-event ${isEditModalOpen ? "modal-content-add-event-right" : ""}`} isOpen={isAddModalOpen || isEditModalOpen} onClose={handleCloseModal}>
                <div className="add-event-modal-header">
                    <div className="add-event-modal-title-row">
                        <h2 className="add-event-modal-title">
                            <span className="add-event-title-icon" aria-hidden="true"></span>
                            {isAddModalOpen ? "Dodaj wydarzenie" : "Edytuj wydarzenie"}
                        </h2>
                        {isEditModalOpen && (
                            <span className={`${isPendingDeactivationRequest ? 'event-status event-status--deactivation-request' : isPendingActivationRequest ? 'event-status event-status--activation-request' : getEventStatusClassName(formData)} add-event-modal-status`}>
                                {isPendingDeactivationRequest
                                    ? 'Wniosek: Dezaktywacja wydarzenia'
                                    : isPendingActivationRequest
                                    ? 'Wniosek: Aktywacja wydarzenia'
                                    : `Wydarzenie ${getEventStatusLabel(formData)}`}
                            </span>
                        )}
                    </div>
                </div>
                <div className="userView-menu add-event-stepper-shell">
                    <div className="add-event-stepper-visual" role="progressbar" aria-valuemin={1} aria-valuemax={2} aria-valuenow={addEventStep}>
                        <div className="add-event-stepper-item">
                            <div className="add-event-step-box active">1</div>
                            <div className="add-event-step-label active">Dane podstawowe</div>
                        </div>
                        <div className="add-event-step-divider">-----</div>
                        <div className="add-event-stepper-item">
                            <div className={`add-event-step-box ${addEventStep === 2 ? "active" : ""}`}>2</div>
                            <div className={`add-event-step-label ${addEventStep === 2 ? "active" : ""}`}>Opis i multimedia</div>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="add-event-modal-scroll-area">
                    {isPendingEditReviewMode && isPendingDeactivationRequest && (
                        <div className="pending-deactivation-banner" role="alert">
                            <div className="pending-deactivation-banner__header">
                                <span className="pending-deactivation-banner__badge">Wniosek organizatora</span>
                                <span className="pending-deactivation-banner__title">Prośba o dezaktywację wydarzenia</span>
                            </div>
                            <p className="pending-deactivation-banner__desc">
                                Organizator zgłosił wniosek o <strong>dezaktywację tego wydarzenia</strong>. Zaakceptowanie zgłoszenia wyłączy widoczność wydarzenia w aplikacji (status zmieni się na <strong>Nieaktywne</strong>). Pozostałe dane wydarzenia nie uległy zmianie.
                            </p>
                        </div>
                    )}
                    {isPendingEditReviewMode && isPendingActivationRequest && (
                        <div className="pending-deactivation-banner pending-activation-banner" role="alert">
                            <div className="pending-deactivation-banner__header">
                                <span className="pending-deactivation-banner__badge pending-activation-banner__badge">Wniosek organizatora</span>
                                <span className="pending-deactivation-banner__title">Prośba o aktywację wydarzenia</span>
                            </div>
                            <p className="pending-deactivation-banner__desc">
                                Organizator zgłosił wniosek o <strong>aktywację tego wydarzenia</strong>. Zaakceptowanie zgłoszenia przywróci widoczność wydarzenia w aplikacji (status zmieni się na <strong>Aktywne</strong>).
                            </p>
                        </div>
                    )}
                    {isPendingEditReviewMode && (
                        <div className="pending-edit-summary" role="status">
                            <strong>Zakres zmian:</strong>{' '}
                            {pendingChangeSummary.length > 0
                                ? pendingChangeSummary.join(', ')
                                : 'brak rozpoznanych zmian w danych wydarzenia.'}
                            <span>
                                {isPendingDeactivationRequest
                                    ? ' Zgłoszenie dotyczy dezaktywacji wydarzenia. Poniżej wyświetlone są aktualne dane wydarzenia.'
                                    : isPendingActivationRequest
                                    ? ' Zgłoszenie dotyczy aktywacji wydarzenia. Poniżej wyświetlone są aktualne dane wydarzenia.'
                                    : ' Zmienione pola są wyróżnione na pomarańczowo; najedź na pole, aby zobaczyć poprzednią wartość.'}
                            </span>
                        </div>
                    )}
                    { modalViewActualTab === "Dane podstawowe" &&
                        <>
                            {isPendingEditReviewMode && (
                                <div className={`flex-container-space-between border-bottom-light add-event-form-row ${pendingReviewChangedFields.isActive ? 'pending-edit-field-changed-row' : ''}`}>
                                    <label className="padding-right-20">Status wydarzenia</label>
                                    <div className="add-event-status-review-row">
                                        <span className={`event-status ${originalFormData?.isActive ? 'event-status--active' : 'event-status--inactive'}`}>
                                            Aktualny: {originalFormData?.isActive ? 'Aktywne' : 'Nieaktywne'}
                                        </span>
                                        {pendingReviewChangedFields.isActive && (
                                            <>
                                                <span className="add-event-status-arrow">&rarr;</span>
                                                <span className={`event-status ${isPendingDeactivationRequest ? 'event-status--deactivation-request' : isPendingActivationRequest ? 'event-status--activation-request' : (formData.isActive ? 'event-status--active' : 'event-status--inactive')} pending-edit-field-changed`}>
                                                    Nowy: {isPendingDeactivationRequest ? 'Nieaktywne (Dezaktywacja)' : isPendingActivationRequest ? 'Aktywne (Aktywacja)' : formData.isActive ? 'Aktywne' : 'Nieaktywne'}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="flex-container-space-between border-bottom-light add-event-form-row">
                                <label htmlFor="name" className="padding-right-20">Nazwa wydarzenia<span className="required-star">*</span></label>
                                <input
                                    className={getChangedFieldClassName("input-field", "name")}
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={getDisplayFieldValue("name")}
                                    title="Wprowadź nazwę wydarzenia"
                                    onChange={isPendingEditReviewMode ? () => {} : handleInputChange}
                                    readOnly={isPendingEditReviewMode}
                                    required
                                    {...getChangedFieldEvents("name")}
                                />
                            </div>
                            <div className="flex-container-space-between border-bottom-light add-event-form-row">
                                <label htmlFor="category" className="padding-right-20">Kategoria wydarzenia<span className="required-star">*</span></label>
                                <select
                                    className={getChangedFieldClassName("input-field", "category")}
                                    type="number"
                                    id="category"
                                    name="category"
                                    value={getDisplayFieldValue("category")}
                                    title="Wybierz kategorię wydarzenia"
                                    onChange={isPendingEditReviewMode ? () => {} : handleInputChange}
                                    disabled={isPendingEditReviewMode}
                                    required
                                    {...getChangedFieldEvents("category")}
                                >
                                    <option value="" disabled>Wybierz z listy</option>
                                    { categories.map(category => (
                                        <option key={category.id} value={category.id}>{category.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex-container-space-between border-bottom-light add-event-form-row add-event-form-row-time">
                                <label htmlFor="startDate" className="padding-right-20">Data rozpoczęcia<span className="required-star">*</span></label>
                                <div className="add-event-date-time-wrap">
                                    <input
                                        className={getChangedFieldClassName("input-field add-event-date-input", "startDate")}
                                        type="date"
                                        id="startDate"
                                        name="startDate"
                                        value={getDisplayFieldValue("startDate")}
                                        onChange={isPendingEditReviewMode ? () => {} : handleInputChange}
                                        title="Wybierz datę wydarzenia"
                                        required
                                        readOnly={isPendingEditReviewMode}
                                        {...getChangedFieldEvents("startDate")}
                                    />
                                    <span className="add-event-time-label">Godzina<span className="required-star">*</span></span>
                                    <div className="add-event-time-split">
                                        <input
                                            className={getChangedFieldClassName("input-field add-event-time-input", "eventTime")}
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={2}
                                            value={(getDisplayFieldValue("eventTime") || "").split(":")[0] || ""}
                                            onChange={isPendingEditReviewMode ? () => {} : (e) => handleSplitTimeChange("eventTime", "hour", e.target.value)}
                                            onFocus={(e) => e.target.select()}
                                            onBlur={isPendingEditReviewMode ? undefined : () => handleSplitTimeBlur("eventTime")}
                                            title="Godzina (HH)"
                                            required
                                            readOnly={isPendingEditReviewMode}
                                            {...getChangedFieldEvents("eventTime")}
                                        />
                                        <span className="add-event-time-colon">:</span>
                                        <input
                                            className={getChangedFieldClassName("input-field add-event-time-input", "eventTime")}
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={2}
                                            value={(getDisplayFieldValue("eventTime") || "").split(":")[1] || ""}
                                            onChange={isPendingEditReviewMode ? () => {} : (e) => handleSplitTimeChange("eventTime", "minute", e.target.value)}
                                            onFocus={(e) => e.target.select()}
                                            onBlur={isPendingEditReviewMode ? undefined : () => handleSplitTimeBlur("eventTime")}
                                            title="Minuty (MM)"
                                            required
                                            readOnly={isPendingEditReviewMode}
                                            {...getChangedFieldEvents("eventTime")}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex-container-space-between border-bottom-light add-event-form-row add-event-form-row-time">
                                <label htmlFor="eventEndDate" className="padding-right-20">Data zakończenia<span className="required-star">*</span></label>
                                <div className="add-event-date-time-wrap">
                                    <input
                                        className={getChangedFieldClassName("input-field add-event-date-input", "eventEndDate")}
                                        type="date"
                                        id="eventEndDate"
                                        name="eventEndDate"
                                        value={getDisplayFieldValue("eventEndDate")}
                                        onChange={isPendingEditReviewMode ? () => {} : handleInputChange}
                                        title="Wybierz datę zakończenia wydarzenia"
                                        min={formData.startDate || undefined}
                                        required
                                        readOnly={isPendingEditReviewMode}
                                        {...getChangedFieldEvents("eventEndDate")}
                                    />
                                    <span className="add-event-time-label">Godzina<span className="required-star">*</span></span>
                                    <div className="add-event-time-split">
                                        <input
                                            className={getChangedFieldClassName("input-field add-event-time-input", "eventEndTime")}
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={2}
                                            value={(getDisplayFieldValue("eventEndTime") || "").split(":")[0] || ""}
                                            onChange={isPendingEditReviewMode ? () => {} : (e) => handleSplitTimeChange("eventEndTime", "hour", e.target.value)}
                                            onFocus={(e) => e.target.select()}
                                            onBlur={isPendingEditReviewMode ? undefined : () => handleSplitTimeBlur("eventEndTime")}
                                            title="Godzina (HH)"
                                            required
                                            readOnly={isPendingEditReviewMode}
                                            {...getChangedFieldEvents("eventEndTime")}
                                        />
                                        <span className="add-event-time-colon">:</span>
                                        <input
                                            className={getChangedFieldClassName("input-field add-event-time-input", "eventEndTime")}
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={2}
                                            value={(getDisplayFieldValue("eventEndTime") || "").split(":")[1] || ""}
                                            onChange={isPendingEditReviewMode ? () => {} : (e) => handleSplitTimeChange("eventEndTime", "minute", e.target.value)}
                                            onFocus={(e) => e.target.select()}
                                            onBlur={isPendingEditReviewMode ? undefined : () => handleSplitTimeBlur("eventEndTime")}
                                            title="Minuty (MM)"
                                            required
                                            readOnly={isPendingEditReviewMode}
                                            {...getChangedFieldEvents("eventEndTime")}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={`flex-container-space-between border-bottom-light add-event-form-row ${pendingReviewChangedFields.ticketInfo ? 'pending-edit-field-changed' : ''}`} {...getChangedFieldEvents("ticketInfo")}>
                                <label htmlFor="ticketInfo" className="padding-right-20">Wstęp<span className="required-star">*</span></label>
                                <div className="add-event-ticket-controls">
                                    <div className="input-field add-event-segmented-control" role="group" aria-label="Wstęp">
                                        <button
                                            type="button"
                                            className={`add-event-segment-button ${formData.ticketInfo === TICKET_PAID ? "active" : ""}`}
                                            onClick={() => !isPendingEditReviewMode && handleTicketInfoSelect(TICKET_PAID)}
                                            disabled={isPendingEditReviewMode}
                                        >
                                            Płatny
                                        </button>
                                        <button
                                            type="button"
                                            className={`add-event-segment-button ${formData.ticketInfo === TICKET_FREE ? "active" : ""}`}
                                            onClick={() => !isPendingEditReviewMode && handleTicketInfoSelect(TICKET_FREE)}
                                            disabled={isPendingEditReviewMode}
                                        >
                                            Bezpłatny
                                        </button>
                                    </div>
                                    <input
                                        className="add-event-ticket-hidden"
                                        type="text"
                                        id="ticketInfo"
                                        name="ticketInfo"
                                        value={formData.ticketInfo}
                                        onChange={handleInputChange}
                                        title="Wpisz informacje o biletach"
                                        required
                                        tabIndex={-1}
                                        aria-hidden="true"
                                    />
                                </div>
                            </div>
                            <div className="flex-container border-bottom-light add-event-form-row">
                                <label htmlFor="ticketPrice" className="padding-right-20">Cena{formData.ticketInfo === TICKET_PAID && <span className="required-star">*</span>}</label>
                                <input
                                    className="input-field"
                                    type="number"
                                    id="ticketPrice"
                                    name="ticketPrice"
                                    value={formData.ticketPrice}
                                    title={isEditModalOpen ? "Cena biletu nie może obecnie zostać zmieniona — backend nie zapisuje tego pola." : "Wprowadź minimalną cenę biletu"}
                                    onChange={isEditModalOpen ? () => {} : handleInputChange}
                                    required={formData.ticketInfo === TICKET_PAID}
                                    readOnly={isEditModalOpen}
                                />
                                {isEditModalOpen && <span className="add-event-field-note">Cena biletu nie jest obecnie edytowalna.</span>}
                            </div>
                            <div className="header-group border-bottom">Lokalizacja</div>

                            <div className="flex-container-space-between border-bottom-light add-event-form-row">
                                <label htmlFor="place" className="padding-right-20">Nazwa miejsca<span className="required-star">*</span></label>
                                <input
                                    className={getChangedFieldClassName("input-field", "place")}
                                    type="text"
                                    id="place"
                                    name="place"
                                    value={getDisplayFieldValue("place")}
                                    title="Wprowadź nazwę miejca"
                                    onChange={isPendingEditReviewMode ? () => {} : handleInputChange}
                                    required
                                    readOnly={isPendingEditReviewMode}
                                    {...getChangedFieldEvents("place")}
                                />
                            </div>
                            <div className="flex-container-space-between border-bottom-light add-event-form-row">
                                <label htmlFor="adres" className="padding-right-20">Adres<span className="required-star">*</span></label>
                                <input
                                    className={getChangedFieldClassName("input-field", "adres")}
                                    type="text"
                                    id="adres"
                                    name="adres"
                                    value={getDisplayFieldValue("adres")}
                                    title="Wprowadź adres"
                                    onChange={isPendingEditReviewMode ? () => {} : handleInputChange}
                                    required
                                    readOnly={isPendingEditReviewMode}
                                    {...getChangedFieldEvents("adres")}
                                />
                            </div>
                            <div className="flex-container-space-between border-bottom-light add-event-form-row">
                                    <label htmlFor="zipCode" className="padding-right-20">Kod pocztowy<span className="required-star">*</span></label>
                                    <input
                                        className={getChangedFieldClassName("input-field", "zipCode")}
                                        type="text"
                                        id="zipCode"
                                        name="zipCode"
                                        value={getDisplayFieldValue("zipCode")}
                                        title="Wprowadź kod pocztowy"
                                        onChange={isPendingEditReviewMode ? () => {} : handleInputChange}
                                        required
                                        readOnly={isPendingEditReviewMode}
                                        {...getChangedFieldEvents("zipCode")}
                                    />
                            </div>
                            <div className="flex-container-space-between border-bottom-light add-event-form-row">
                                    <label htmlFor="city" className="padding-right-20">Miejscowość<span className="required-star">*</span></label>
                                    <input
                                        className={getChangedFieldClassName("input-field", "city")}
                                        type="text"
                                        id="city"
                                        name="city"
                                        value={getDisplayFieldValue("city")}
                                        title="Wprowadź miejscowość"
                                        onChange={isPendingEditReviewMode ? () => {} : handleInputChange}
                                        required
                                        readOnly={true}
                                        {...getChangedFieldEvents("city")}
                                    />
                            </div>
                        </>}

                    {isSecondStepActive &&
                        <div style={{ position: 'relative' }}>
                            <label className="description-small-padding" htmlFor="description-add">Opis<span className="required-star">*</span></label>
                            <div className="flex-line border-bottom-light" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <textarea
                                    type="text"
                                    id="description"
                                    name="description"
                                    value={getDisplayFieldValue("description")}
                                    onChange={isPendingEditReviewMode ? () => {} : handleInputChange}
                                    required
                                    className={getChangedFieldClassName("event-description-add", "description")}
                                    style={{ flexGrow: 1 }}
                                    readOnly={isPendingEditReviewMode}
                                    {...getChangedFieldEvents("description")}
                                />
                            </div>
                            <div className={`flex-line border-bottom-light add-event-tags-row ${pendingReviewChangedFields.tags ? 'pending-edit-field-changed' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }} {...getChangedFieldEvents("tags")}>
                                <label className="padding-right-20" htmlFor="tagList">Tagi</label>
                                <div id="tagList" className="tag-list add-event-tags-list" >
                                    {formData.tags.length > 0 ? (
                                        formData.tags.map((tag, index) => (
                                            <span key={`${tag}-${index}`} className="tag-item">
                                                {tag}
                                                <button
                                                    type="button"
                                                    className="button-orange-rounded-very-tiny-textonly"
                                                    onClick={() => !isPendingEditReviewMode && handleRemoveTag(index)}
                                                    title="Usun tag"
                                                    disabled={isPendingEditReviewMode}
                                                >
                                                    x
                                                </button>
                                            </span>
                                        ))
                                    ) : (
                                        <span>Brak tagów</span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="button-orange-rounded-tiny-plus"
                                    onClick={() => !isPendingEditReviewMode && setIsTagModalOpen(true)}
                                    title="Dodaj tag"
                                    disabled={isPendingEditReviewMode}
                                >
                                    +
                                </button>
                            </div>
                        </div>}



                    {isSecondStepActive &&
                        <>
                            <div className={`flex-line-add-modal add-event-upload-row ${hasMainImageChange ? 'pending-edit-field-changed' : ''}`}>
                                <label htmlFor="mainImageInput" className="padding-right-20">Zdjęcie główne<span className="required-star">*</span></label>
                                <input
                                    ref={mainImageInputRef}
                                    className="add-event-hidden-file-input"
                                    type="file"
                                    id="mainImageInput"
                                    name="mainImageInput"
                                    onChange={isPendingEditReviewMode ? () => {} : handleMainImageChange}
                                    title="Wybierz plik obrazka głównego"
                                    accept="image/png, image/jpeg, image/webp"
                                    disabled={isPendingEditReviewMode}
                                />
                                <div
                                    className="add-event-dropzone"
                                    onDragOver={isPendingEditReviewMode ? undefined : handleDragOver}
                                    onDrop={isPendingEditReviewMode ? undefined : (e) => handleDrop(e, "main")}
                                >
                                    <button
                                        type="button"
                                        className="add-event-upload-button"
                                        onClick={() => !isPendingEditReviewMode && mainImageInputRef.current?.click()}
                                        disabled={isPendingEditReviewMode}
                                    >
                                        Dodaj
                                    </button>
                                    <span className="add-event-dropzone-hint">lub przeciagnij i upusc plik tutaj</span>
                                </div>
                                <img
                                    src={mainImage.preview || defaultPlaceholder}
                                    className="imgMiniature add-event-main-preview"
                                    alt="Podgląd głównego zdjęcia"
                                />
                            </div>
                            {hasMainImageChange && (
                                <div className="pending-image-changes-panel">
                                    <div className="pending-image-changes-title">Zmiana – zdjęcie</div>
                                    <div className="pending-image-changes-row is-changed">
                                        <span>Główne:</span>
                                        <code>{normalizeImageId(originalImageState?.mainId) || 'brak'}</code>
                                        <span className="pending-image-changes-arrow">→</span>
                                        <code>{normalizeImageId(imageHashCode || mainImage?.id) || 'brak'}</code>
                                    </div>
                                    <div className="pending-image-changes-note">
                                        W sekcji „Opis i multimedia” zmieniono zdjęcie główne wydarzenia.
                                    </div>
                                </div>
                            )}
                            <div className="flex-line-add-modal border-bottom-light add-event-upload-row">
                                <label htmlFor="additionalImagesInput" className="padding-right-20">Zdjęcia dodatkowe</label>
                                <input
                                    ref={additionalImagesInputRef}
                                    className="add-event-hidden-file-input"
                                    type="file"
                                    id="additionalImagesInput"
                                    name="additionalImagesInput"
                                    onChange={isPendingEditReviewMode ? () => {} : handleAdditionalImagesChange}
                                    multiple // Obsługa wielu plików
                                    title="Wybierz pliki obrazków dodatkowych"
                                    accept="image/png, image/jpeg, image/webp"
                                    disabled={isPendingEditReviewMode}
                                />
                                <div
                                    className="add-event-dropzone"
                                    onDragOver={isPendingEditReviewMode ? undefined : handleDragOver}
                                    onDrop={isPendingEditReviewMode ? undefined : (e) => handleDrop(e, "additional")}
                                >
                                    <button
                                        type="button"
                                        className="add-event-upload-button"
                                        onClick={() => !isPendingEditReviewMode && additionalImagesInputRef.current?.click()}
                                        disabled={isPendingEditReviewMode}
                                    >
                                        Dodaj
                                    </button>
                                    <span className="add-event-dropzone-hint">lub przeciagnij i upusc pliki tutaj</span>
                                </div>
                            </div>
                            <div className="flex-line2 border-bottom-light">
                                {additionalImages.length > 0 ? (
                                    additionalImages.map((imgData, index) => (
                                        <div key={index} className="image-container">
                                            <img
                                                src={imgData.preview}
                                                className="imgMiniature"
                                                alt={`Dodatkowe ${index}`}
                                            />

                                            {/* Nakładka widoczna po najechaniu myszką */}
                                            <div className="image-overlay ">
                                                {/* Górna część: Usuń */}
                                                <div
                                                    className="overlay-action overlay-action-top  delete"
                                                    onClick={() => !isPendingEditReviewMode && handleRemoveImage(index)}
                                                    title="Usuń to zdjęcie"
                                                >
                                                    Usuń
                                                </div>

                                                {/* Dolna część: Ustaw główne */}
                                                <div
                                                    className="overlay-action overlay-action-bottom set-main"
                                                    onClick={() => !isPendingEditReviewMode && handleSwapImage(index)}
                                                    title="Ustaw jako zdjęcie główne"
                                                >
                                                    Główne
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    // Wyświetlanie placeholderów gdy brak zdjęć dodatkowych
                                    <>
                                        {/*<img src={defaultPlaceholder} className="imgMiniature" alt="puste" />*/}
                                    </>
                                )}
                            </div>
                        </>
                    }
                    </div>

                <div className="modal-margin-top-20 modal-actions-stack">
                    {isEditModalOpen && isPendingApproval && !isPendingEditReviewMode && (
                        <div className="modal-actions-approval-row">
                            <button
                                className="button-orange-rounded-tiny add-event-status-toggle-button"
                                type="button"
                                onClick={handleApproveEvent}
                                disabled={isStatusActionLoading}
                            >
                                Zaakceptuj
                            </button>
                            <button
                                className="button-orange-rounded-tiny add-event-status-toggle-button"
                                type="button"
                                onClick={handleRejectEvent}
                                disabled={isStatusActionLoading}
                            >
                                {'Odrzu\u0107'}
                            </button>
                        </div>
                    )}
                    <div className="modal-actions">
                    <div className="modal-actions-left">
                        <button className="button-orange-rounded-tiny-textonly add-event-cancel-button" type="button" onClick={handleCloseModal}>
                            Anuluj
                        </button>
                        <button className="button-orange-rounded-tiny add-event-back-button" disabled={isFirstTab} type="button" onClick={handlePrevButtonClick}>
                            <span className="fontX-tiny">&larr;&nbsp;</span>Wstecz
                        </button>
                    </div>
                    <div className="modal-actions-center">
                        {isEditModalOpen && !isPendingEditReviewMode && (
                            <button
                                className="button-orange-rounded-tiny add-event-status-toggle-button"
                                type="button"
                                onClick={handleToggleEventActive}
                                disabled={isStatusActionLoading || isPendingApproval || isEventEnded(formData)}
                            >
                                {formData.isActive ? 'Dezaktywuj' : 'Aktywuj'}
                            </button>
                        )}
                    </div>
                    <div className="modal-actions-right">
                        {isPendingEditReviewMode ? (
                            <>
                                <button className="button-orange-rounded-tiny add-event-next-button" disabled={isLastTab} type="button" onClick={handleNextButtonClick}>
                                    Dalej<span className="fontX-tiny add-event-next-chevron">&nbsp;&rarr;</span>
                                </button>
                                <button
                                    className="button-orange-rounded-tiny add-event-status-toggle-button add-event-reject-pending-button"
                                    type="button"
                                    onClick={handleRejectPendingEditFromReview}
                                    disabled={isStatusActionLoading}
                                >
                                    {isPendingDeactivationRequest || isPendingActivationRequest ? 'Odrzuć wniosek' : 'Odrzuć zmianę'}
                                </button>
                                <button
                                    className="button-orange-backgrounded-tiny add-event-approve-pending-button"
                                    type="button"
                                    onClick={handleApprovePendingEditFromReview}
                                    disabled={isStatusActionLoading}
                                >
                                    {isPendingDeactivationRequest
                                        ? 'Zaakceptuj dezaktywację'
                                        : isPendingActivationRequest
                                        ? 'Zaakceptuj aktywację'
                                        : 'Zaakceptuj zmianę'}
                                </button>
                            </>
                        ) : (
                            <>
                                <button className="button-orange-rounded-tiny add-event-next-button" disabled={isLastTab} type="button" onClick={handleNextButtonClick}>
                                    Dalej<span className="fontX-tiny add-event-next-chevron">&nbsp;&rarr;</span>
                                </button>
                                <button type="submit" disabled={isSubmitDisabled} className="button-orange-backgrounded-tiny">
                                    {/*<span className="fontX-tiny">&#8862;&nbsp;</span>*/}
                                    <div className="step-box-plus"></div> {isAddModalOpen ? "Dodaj wydarzenie" : ""} {isEditModalOpen ? "Zapisz zmiany": ""}
                                </button>
                            </>
                        )}
                    </div>
                    </div>
                </div>
                </form>
            </Modal> }
        </>
    );
}

export default AddEvent;
